import type { RefObject } from "react";
import type { AgGridReact } from "~/components/grid/AgGrid";
import type { SetFilterLabelResolver } from "~/helpers/grid/filterModelToText";

import { useCallback, useState } from "react";

import { shouldShowFilterBar } from "~/helpers/grid/filterBarPlacement";
import useFilterBarPlacement from "~/hooks/useFilterBarPlacement";
import useMruFilters from "~/hooks/useMruFilters";

const useGridFilterBar = <TData>(
	gridDataKey: string,
	gridRef: RefObject<AgGridReact<TData>>,
	labelResolver?: SetFilterLabelResolver
) => {
	const [activeFilterModel, setActiveFilterModel] = useState<Record<string, unknown>>({});
	const [filtersPaused, setFiltersPaused] = useState(false);
	const { cyclePlacement, placement: filterBarPlacement } = useFilterBarPlacement();
	const mruFilters = useMruFilters(gridDataKey);
	const activeFilterCount = Object.keys(activeFilterModel).length;
	const updateFilterModel = useCallback(() => {
		const model = gridRef.current?.api.getFilterModel();
		setActiveFilterModel({ ...model });
		return model;
	}, [gridRef]);
	const recordFilterChange = useCallback(() => {
		const model = updateFilterModel();
		if (model && Object.keys(model).length > 0) mruFilters.addEntry(model, labelResolver);
	}, [labelResolver, mruFilters.addEntry, updateFilterModel]);
	const showFilterBar =
		shouldShowFilterBar(filterBarPlacement, activeFilterCount, mruFilters.entries.length) ||
		(filtersPaused && filterBarPlacement !== "hide");
	return {
		activeFilterCount,
		cyclePlacement,
		filterBarPlacement,
		mruFilters,
		recordFilterChange,
		setFiltersPaused,
		showFilterBar,
		updateFilterModel
	};
};

export default useGridFilterBar;
